import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SnakeGame.css';

const GRID = 18;
const SPEEDS = { easy: 150, medium: 100, hard: 60 };

function getInitialState() {
  return {
    snake: [{ x: 9, y: 9 }, { x: 8, y: 9 }, { x: 7, y: 9 }],
    food: { x: 14, y: 9 },
    direction: 'RIGHT',
    gameOver: false,
    started: false,
    score: 0,
    highScore: parseInt(localStorage.getItem('demohub_snake_best') || '0', 10),
  };
}

function randomFood(snake) {
  const empty = [];
  for (let x = 0; x < GRID; x++) {
    for (let y = 0; y < GRID; y++) {
      if (!snake.some((s) => s.x === x && s.y === y)) empty.push({ x, y });
    }
  }
  return empty[Math.floor(Math.random() * empty.length)];
}

function SnakeGame() {
  const navigate = useNavigate();
  const [state, setState] = useState(getInitialState);
  const [speed, setSpeed] = useState('medium');
  const [isPaused, setIsPaused] = useState(false);
  const [phoneOn, setPhoneOn] = useState(true);
  const dirRef = useRef('RIGHT');
  const gameLoop = useRef(null);

  const moveSnake = useCallback(() => {
    setState((prev) => {
      if (prev.gameOver || !prev.started) return prev;
      const dir = dirRef.current;
      const head = { ...prev.snake[0] };
      switch (dir) {
        case 'UP': head.y -= 1; break;
        case 'DOWN': head.y += 1; break;
        case 'LEFT': head.x -= 1; break;
        case 'RIGHT': head.x += 1; break;
        default: break;
      }
      if (head.x < 0 || head.x >= GRID || head.y < 0 || head.y >= GRID) return { ...prev, gameOver: true };
      if (prev.snake.some((s) => s.x === head.x && s.y === head.y)) return { ...prev, gameOver: true };
      const newSnake = [head, ...prev.snake];
      let newFood = prev.food;
      let newScore = prev.score;
      if (head.x === prev.food.x && head.y === prev.food.y) {
        newScore += 10;
        newFood = randomFood(newSnake);
      } else {
        newSnake.pop();
      }
      const highScore = Math.max(newScore, prev.highScore);
      if (highScore > prev.highScore) {
        try { localStorage.setItem('demohub_snake_best', highScore.toString()); } catch {}
      }
      return { ...prev, snake: newSnake, food: newFood, score: newScore, highScore };
    });
  }, []);

  useEffect(() => {
    if (state.started && !state.gameOver && !isPaused && phoneOn) {
      gameLoop.current = setInterval(moveSnake, SPEEDS[speed]);
    }
    return () => clearInterval(gameLoop.current);
  }, [state.started, state.gameOver, isPaused, speed, phoneOn, moveSnake]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!phoneOn) return;
      const map = { ArrowUp: 'UP', ArrowDown: 'DOWN', ArrowLeft: 'LEFT', ArrowRight: 'RIGHT' };
      const opposite = { UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT' };
      const dir = map[e.key];
      if (dir) {
        e.preventDefault();
        if (dir !== opposite[dirRef.current]) dirRef.current = dir;
        if (!state.started) setState((s) => ({ ...s, started: true }));
      }
      if (e.key === ' ') {
        e.preventDefault();
        if (state.gameOver) { dirRef.current = 'RIGHT'; setState(getInitialState()); }
        else setIsPaused((p) => !p);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [state.started, state.gameOver, phoneOn]);

  const handleDPad = (dir) => {
    if (!phoneOn) return;
    const opposite = { UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT' };
    if (dir !== opposite[dirRef.current]) dirRef.current = dir;
    if (!state.started) setState((s) => ({ ...s, started: true }));
  };

  const handleRestart = () => {
    dirRef.current = 'RIGHT';
    setState(getInitialState());
    setIsPaused(false);
  };

  const handleNavCenter = () => {
    if (!phoneOn) return;
    if (state.gameOver) handleRestart();
    else setIsPaused(p => !p);
  };

  return (
    <div className="snake-app">
      <div className="snake-topbar">
        <div className="snake-topbar-left">
          <button className="snake-back-btn" onClick={() => navigate('/demo/games')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="snake-page-title">Nokia Snake</h1>
        </div>
        <div className="snake-topbar-right">
          <div className="snake-speed-toggle">
            {Object.keys(SPEEDS).map((s) => (
              <button key={s} className={`snake-speed-btn ${speed === s ? 'active' : ''}`}
                onClick={() => setSpeed(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="nokia-scene">
        {/* The actual Nokia 1100 phone */}
        <div className="nokia-device">
          {/* Phone image background */}
          <img
            src="https://raw.githubusercontent.com/khang-nd/public-assets/main/Nokia1100.png"
            alt="Nokia 1100"
            className="nokia-img"
            draggable="false"
          />

          {/* Screen overlay — positioned on the phone's screen area */}
          <div className={`nokia-screen-area ${phoneOn ? 'on' : 'off'}`}>
            <div className="nokia-lcd">
              {phoneOn && (
                <>
                  {/* LCD Top bar */}
                  <div className="nokia-hud">
                    <span>Score:{state.score}</span>
                    <span>Best:{state.highScore}</span>
                  </div>

                  {/* Snake grid */}
                  <div className="nokia-game">
                    <div className="nokia-grid" style={{ '--g': GRID }}>
                      {Array.from({ length: GRID * GRID }).map((_, i) => {
                        const x = i % GRID;
                        const y = Math.floor(i / GRID);
                        const isHead = state.snake[0]?.x === x && state.snake[0]?.y === y;
                        const isSnake = state.snake.some((s) => s.x === x && s.y === y);
                        const isFood = state.food.x === x && state.food.y === y;
                        return <div key={i} className={`npx ${isHead ? 'head' : isSnake ? 'body' : ''} ${isFood ? 'food' : ''}`} />;
                      })}
                    </div>

                    {/* Overlays */}
                    {!state.started && !state.gameOver && (
                      <div className="nokia-scr-overlay">
                        <span className="nokia-big-txt">SNAKE</span>
                        <span className="nokia-sm-txt">Press direction to play</span>
                      </div>
                    )}
                    {state.gameOver && (
                      <div className="nokia-scr-overlay">
                        <span className="nokia-big-txt">GAME OVER</span>
                        <span className="nokia-sm-txt">Score: {state.score}</span>
                        <span className="nokia-sm-txt">Press center to retry</span>
                      </div>
                    )}
                    {isPaused && !state.gameOver && (
                      <div className="nokia-scr-overlay">
                        <span className="nokia-big-txt">PAUSED</span>
                      </div>
                    )}
                  </div>

                  {/* LCD Bottom bar */}
                  <div className="nokia-hud bottom">
                    <span>Len:{state.snake.length}</span>
                    <span>{speed.toUpperCase()}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Clickable button zones — invisible overlays on the phone image */}
          {/* Power button */}
          <button className="nokia-btn nokia-btn-pow" onClick={() => setPhoneOn(p => !p)} title="Power"></button>

          {/* Navigation: up, down, left, right, center */}
          <button className="nokia-btn nokia-btn-up" onClick={() => handleDPad('UP')} title="Up"></button>
          <button className="nokia-btn nokia-btn-down" onClick={() => handleDPad('DOWN')} title="Down"></button>
          <button className="nokia-btn nokia-btn-nav" onClick={handleNavCenter} title="OK / Pause"></button>

          {/* Left soft key — clear/back */}
          <button className="nokia-btn nokia-btn-clr" onClick={() => { if (phoneOn) handleRestart(); }} title="Clear"></button>

          {/* Number keys for fun */}
          <div className="nokia-numpad-zone">
            {[1,2,3,4,5,6,7,8,9,'*',0,'#'].map((k, i) => {
              const dirMap = { 2: 'UP', 4: 'LEFT', 6: 'RIGHT', 8: 'DOWN' };
              return (
                <button
                  key={i}
                  className="nokia-btn nokia-btn-num"
                  onClick={() => dirMap[k] ? handleDPad(dirMap[k]) : null}
                  title={String(k)}
                ></button>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        <div className="nokia-side-info">
          <div className="nokia-info-card">
            <h3>Controls</h3>
            <div className="nokia-info-row"><span className="nokia-key-label">↑ ↓ ← →</span><span>Move snake</span></div>
            <div className="nokia-info-row"><span className="nokia-key-label">Space</span><span>Pause / Restart</span></div>
            <div className="nokia-info-row"><span className="nokia-key-label">2 4 6 8</span><span>Numpad move</span></div>
          </div>
          <div className="nokia-info-card">
            <h3>Stats</h3>
            <div className="nokia-info-row"><span>Score</span><strong>{state.score}</strong></div>
            <div className="nokia-info-row"><span>High Score</span><strong>{state.highScore}</strong></div>
            <div className="nokia-info-row"><span>Length</span><strong>{state.snake.length}</strong></div>
            <div className="nokia-info-row"><span>Speed</span><strong>{speed}</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SnakeGame;
